import * as React from "react";
import { createContext, useState, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { defaultTheme as amplifyDefaultTheme } from "@aws-amplify/ui-react";
import deepmerge from "deepmerge";

export const ThemeContext = createContext();

const defaultTheme = createTheme(
  deepmerge(createTheme(), {
    palette: {
      mode: "dark",
      foreground: {
        default: "#444444",
        main: "#555555",
        light: "#666666",
        dark: "#333333",
      },
      background: {
        default: "#111111",
        paper: "#111111",
        main: "#111111",
        light: "#252525",
        dark: "#000000",
      },
      appbar: {
        background: "#241816",
        text: "#eedcaa",
      },
    },
  })
);

const themes = {
  default: defaultTheme,
  reefscape: createTheme(
    deepmerge(defaultTheme, {
      palette: {
        mode: "dark",
        primary: {
          main: "#9f3593",
          light: "#b843ac",
          dark: "#8c2e81",
        },
        secondary: {
          main: "#00caac",
          light: "#11edcc",
          dark: "#02ad94",
        },
        text: {
          primary: "#e8f3f4",
          secondary: "#cbf2eb",
        },
        border: {
          main: "#66666f",
        },
        red: {
          main: "#f44336",
          light: "#fa7c73",
        },
        blue: {
          main: "#00b2ca",
          light: "#33c2d6",
        },
        accent: {
          main: "#ffd93d",
          light: "#ffe06b",
          dark: "#e6c235",
        },
        gray: {
          main: "#333333",
        },
        manualColor: {
          main: "#ffffff",
        },
        black: {
          main: "#080808",
        },
      },
    })
  ),
  analysis: createTheme(
    deepmerge(defaultTheme, {
      palette: {
        mode: "dark",
        primary: {
          main: "#bf8826",
          light: "#d99d32",
          dark: "#9e6e18",
        },
        secondary: {
          main: "#9A1B1B", // Darker red
          light: "#B22222",
          dark: "#7A1212",
        },
        text: {
          primary: "#E0E0E0", // Slightly off-white for readability
          secondary: "#A0A0A0",
        },
        foreground: {
          main: "#242424",
        },
        border: {
          main: "#292929",
        },
        red: {
          main: "#a02a2a", // Darker red
          light: "#b84b4b",
        },
        blue: {
          main: "#1e88e5", // Darker blue
          light: "#4a9ff3",
        },
        accent: {
          main: "#A63C1A", // Darker burnt orange for an accent
          light: "#BF4F2A",
          dark: "#872C0F",
        },
        gray: {
          main: "#222222",
        },
        manualColor: {
          main: "#ffffff",
        },
      },
    })
  ),
};

defaultTheme.typography.fontFamily = "Poppins, sans-serif";

export const ThemeManager = ({ children }) => {
  const [themeName, setThemeName] = useState("reefscape");

  const theme = useMemo(
    () => themes[themeName] || amplifyDefaultTheme[themeName],
    [themeName]
  );

  const toggleTheme = (name) => {
    setThemeName(name);
  };

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
